import React, { useMemo, useState } from 'react';
import { Container, Row } from 'reactstrap';
import { ClipLoader } from '../../components/ClipLoader';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../state/ApplicationState';
import { LicencesTable } from './components/LicencesTable';
import { LicencesToolbar } from './components/LicencesToolbar';
import { TachoConfirmationModal } from '../../components/TachoConfirmationModal';
import { searchInObject } from '../../../core/helpers/search';
import { LicenseModel } from '../../../core/domainModels/licences/LicenseModel';
import {
    allowCarStatusCounterResetThunk,
    fetchLicenceThunk,
    removeLicenceVersionThunk,
    sendExistingLicenceViaEmailThunk,
    updateLicenceThunk,
} from '../../../state/licences/reducer';
import { GenerateLicenceModal } from '../../modals/licences/GenerateLicenceModal';
import { UpdateLicenceVersionModal } from '../../modals/licences/UpdateLicenceVersionModal';
import { downloadLicense } from '../../../core/api/endpoints/licences/licensesApi';
import { DispatchType } from '../../../state/configureStore';
import { TachoInformationModal } from '../../components/TachoInformationModal/informationModal';
import { LicenseStatusEnum } from '../../../core/domainModels/licences/LicenseStatus';
import { AddLicenceVersionModal } from '../../modals/licences/AddLicenceVersionModal';
import { LicenseVersion } from '../../../core/domainModels/licences/LicenseVersion';
import { SetCarsLimitModal } from '../../modals/licences/SetCarsLimitModal';
import { ShowVersionsModal } from '../../modals/licences/ShowVersionsModal';

export const LicencesPage = () => {
    const dispatch = useDispatch<DispatchType>();
    const licences = useSelector((state: ApplicationState) => state.licences.licences);
    const fetchLicencesPending = useSelector((state: ApplicationState) => state.licences.fetchLicencePending);
    const licenceVersionPending = useSelector((state: ApplicationState) => state.licences.licenceVersionPending);
    const licenceVersionError = useSelector((state: ApplicationState) => state.licences.licenceVersionError);
    const [editingLicenseContext, setEditingLicenseContext] = useState<{ licenseNumber: string; version: LicenseVersion } | null>(
        null,
    );
    const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false);
    const [isOpenAddLicenceVersionModal, setIsOpenAddLicenceVersionModal] = useState(false);
    const [isOpenGenerateLicenceModal, setIsOpenGenerateLicenceModal] = useState(false);
    const [isOpenUpdateLicenceModal, setIsOpenUpdateLicenceModal] = useState(false);
    const [isShowVersionsOpen, setIsShowVersionsOpen] = useState(false);
    const [isOpenSendEmailModal, setIsOpenSendEmailModal] = useState(false);
    const [isOpenCarLimitModal, setIsOpenCarLimitModal] = useState(false);
    const [selectedLicenses, setSelectedLicenses] = useState<Array<any>>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const clients = useSelector((state: ApplicationState) => state.clients.clients);
    const licencesToShow = useMemo(() => {
        return searchQuery
            ? licences.filter((users: LicenseModel) =>
                  searchInObject(
                      {
                          ...users,
                          nip: clients.find(item => users.clientId === item.id)?.nip,
                          clientId: clients.find(item => users.clientId === item.id)?.name,
                      },
                      searchQuery,
                  ),
              )
            : licences;
    }, [licences, searchQuery, clients]);
    const isSelectedLicence = useMemo(() => {
        return selectedLicenses.length > 0;
    }, [selectedLicenses]);
    const selectedLicenceFromStore = useMemo(
        () => licences.find(l => l.licenseNumber === selectedLicenses[0]?.licenseNumber),
        [licences, selectedLicenses],
    );

    return (
        <Container fluid className="flexColumnFullHeightContainer">
            <LicencesToolbar
                selectedLicences={selectedLicenses}
                openAddLicenceVersionModal={() => setIsOpenAddLicenceVersionModal(!isOpenAddLicenceVersionModal)}
                openGenerateLicenceModal={() => setIsOpenGenerateLicenceModal(!isOpenGenerateLicenceModal)}
                sendLicenceViaEmailToClient={() =>
                    isSelectedLicence &&
                    dispatch(sendExistingLicenceViaEmailThunk(selectedLicenses[0].licenseNumber)).then(response => {
                        if (response) {
                            setIsOpenSendEmailModal(true);
                        }
                    })
                }
                openConfirmRemoveModal={() => setIsConfirmRemoveModalOpen(!isConfirmRemoveModalOpen)}
                onRefresh={() => dispatch(fetchLicenceThunk())}
                onSearchQueryChanged={setSearchQuery}
                changeLicenceStatus={status =>
                    dispatch(
                        updateLicenceThunk({
                            licence: {
                                licenseNumber: selectedLicenses[0].licenseNumber,
                                status: status ? LicenseStatusEnum.Active : LicenseStatusEnum.Blocked,
                            },
                            licenseNumber: selectedLicenses[0].licenseNumber,
                        }),
                    ).then(() => dispatch(fetchLicenceThunk()))
                }
                downloadLicence={() =>
                    isSelectedLicence &&
                    downloadLicense(selectedLicenses[0].licenseNumber).then(link => {
                        link.click();
                    })
                }
                openUpdateLicenceModal={() => {
                    if (selectedLicenses[0]?.licenseVersions?.length > 0) {
                        setIsShowVersionsOpen(true);
                    }
                }}
                openCarLimitModal={() => setIsOpenCarLimitModal(!isOpenCarLimitModal)}
                allowCarStatusCounterReset={() =>
                    dispatch(allowCarStatusCounterResetThunk(selectedLicenses[0].licenseNumber)).then(() =>
                        dispatch(fetchLicenceThunk()),
                    )
                }
            />
            <Row className="ag-theme-balham tabTable flexGrowScrollableChild g-0">
                {fetchLicencesPending ? (
                    <ClipLoader />
                ) : (
                    <LicencesTable
                        onLicenceDoubleClicked={() => {
                            if (selectedLicenses[0]?.licenseVersions?.length > 0) {
                                setIsShowVersionsOpen(true);
                            }
                        }}
                        onSelectionChanged={selectedLicences => setSelectedLicenses(selectedLicences)}
                        licences={licencesToShow}
                        clients={clients}
                        searchQuery={searchQuery}
                    />
                )}
            </Row>
            <TachoConfirmationModal
                isOpen={isConfirmRemoveModalOpen}
                onSubmit={() => {
                    selectedLicenses[0] &&
                        dispatch(
                            removeLicenceVersionThunk({
                                licenseNumber: selectedLicenses[0].licenseNumber,
                                licenseVersionId: selectedLicenses[0].licenseVersions.reduce(
                                    (a: LicenseVersion, b: LicenseVersion) => (a.validFrom > b.validFrom ? a : b),
                                ).id,
                            }),
                        ).then(res => {
                            setSelectedLicenses([]);
                            dispatch(fetchLicenceThunk());
                        });
                }}
                onToggle={() => setIsConfirmRemoveModalOpen(!isConfirmRemoveModalOpen)}
                message={
                    selectedLicenses[0]?.licenceVersions?.length !== 1
                        ? 'licencesVersionRemoveConfirmationMessage'
                        : 'licencesRemoveConfirmationMessage'
                }
                pending={licenceVersionPending}
                size={'md'}
                errorToTranslate={licenceVersionError}
                title={selectedLicenses[0]?.licenceVersions?.length !== 1 ? 'licenceVersionDeleteTitle' : 'licenceDeleteTitle'}
            />
            <GenerateLicenceModal
                isOpen={isOpenGenerateLicenceModal}
                onToggle={() => setIsOpenGenerateLicenceModal(!isOpenGenerateLicenceModal)}
            />
            {selectedLicenses[0] && (
                <AddLicenceVersionModal
                    isOpen={isOpenAddLicenceVersionModal}
                    licence={selectedLicenses[0]}
                    onToggle={() => setIsOpenAddLicenceVersionModal(!isOpenAddLicenceVersionModal)}
                />
            )}
            {selectedLicenceFromStore && isShowVersionsOpen && (
                <ShowVersionsModal
                    licenseVersions={selectedLicenceFromStore.licenseVersions}
                    isOpen={isShowVersionsOpen}
                    onToggle={() => setIsShowVersionsOpen(false)}
                    onEditVersion={version => {
                        setEditingLicenseContext({
                            licenseNumber: selectedLicenceFromStore.licenseNumber,
                            version,
                        });
                        setIsOpenUpdateLicenceModal(true);
                    }}
                />
            )}
            {editingLicenseContext && (
                <UpdateLicenceVersionModal
                    licenseVersion={editingLicenseContext.version}
                    licenseNumber={editingLicenseContext.licenseNumber}
                    isOpen={isOpenUpdateLicenceModal}
                    onToggle={() => {
                        setIsOpenUpdateLicenceModal(false);
                        setEditingLicenseContext(null);
                    }}
                />
            )}
            <TachoInformationModal
                id={'sendLicenceToClientViaEmail'}
                modalTitle={'licencesSendLicenceToTheClientEmail'}
                modalBody={'licencesSendLicenceSuccess'}
                isOpen={isOpenSendEmailModal}
                onToggle={() => setIsOpenSendEmailModal(!isOpenSendEmailModal)}
            />

            <SetCarsLimitModal
                isOpen={isOpenCarLimitModal}
                limit={10}
                onSave={(limit: number) =>
                    dispatch(
                        allowCarStatusCounterResetThunk({ licenceNumber: selectedLicenses[0].licenseNumber, limit: limit }),
                    ).then(() => dispatch(fetchLicenceThunk()))
                }
                onToggle={() => setIsOpenCarLimitModal(!isOpenCarLimitModal)}
            />
        </Container>
    );
};
